import React from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import DeleteForeverOutlinedIcon from '@mui/icons-material/DeleteForeverOutlined';
import styles from './Team.module.scss';
import Table, { MEETING_STATUS } from '../../../../Common/Table/Table/Table';
import useApiData from '../../../../../../hooks/useApiData';
import Loading from '../../../../Common/Loading/Loading';
import { getErrorComponentFromHttpError } from '../../../../Common/Error/Error';
import ConfirmationDialog from '../../../../Common/ConfirmationDialog/ConfirmationDialog';
import APIClient from '../../../../../../util/APIClient';
import FormAlert from '../../../../Common/FormAlert/FormAlert';
import ScoreDisplay from '../../../../Common/ScoreDisplay/ScoreDisplay';
import useAuth from '../../../../../../hooks/useAuth';
import { v4 as uuid } from 'uuid';

function teamMemberSortFn(a, b) {
  if (a.isLead && b.isLead) {
    if (a.fullName < b.fullName) {
      return -1;
    }
    if (a.fullName > b.fullName) {
      return 1;
    }
    return 0;
  }
  if (a.isLead) {
    return -1;
  }
  if (b.isLead) {
    return 1;
  }
  if (a.fullName < b.fullName) {
    return -1;
  }
  if (a.fullName > b.fullName) {
    return 1;
  }
  return 0;
}

export default function Team() {
  const navigate = useNavigate();
  const { groups } = useAuth();
  const { teamId } = useParams();
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = React.useState(false);
  const [isDeleting, setIsDeleting] = React.useState(false);
  const [formAlert, setFormAlert] = React.useState(null);
  const { data, error, isLoading } = useApiData({
    path: `/administrator/teams/${teamId}`,
    params: {
      includeGoals: true,
      includeTeamMembers: true,
      includeMeetingSummaries: true,
    },
  });

  const handleDelete = async () => {
    setIsDeleting(true);
    try {
      await APIClient.delete(`/administrator/teams/${teamId}`);
      setFormAlert({
        severity: 'success',
        title: 'Team deleted successfully!',
        message: 'Your team has been deleted successfully. You will be redirected shortly.',
      });
      setTimeout(() => {
        navigate(`/administrator/teams`);
      }, 3000);
    } catch {
      setFormAlert({
        severity: 'error',
        title: 'Could not delete team!',
        message: 'Something went wrong during team deletion. Please contact the administrator for further support.',
      });
    } finally {
      setIsDeleting(false);
    }
  };

  if (isLoading) {
    return <Loading message="Fetching team..." />;
  }

  if (error || data === null) {
    return getErrorComponentFromHttpError(error);
  }

  const userData = data.teamMembers.map((tm) => ({
    ...tm,
    fullName: `${tm.firstName} ${tm.lastName}`,
    performanceScore: (
      <ScoreDisplay
        subjectType="teamMember"
        scoreType="performance"
        teamId={teamId}
        userId={tm.cognitoId}
        fromDate={new Date(new Date().getTime() - 6 * 7 * 24 * 3600 * 1000).toISOString().substring(0, 10)}
        toDate={new Date().toISOString().substring(0, 10)}
      />
    ),
    processAdoptionScore: (
      <ScoreDisplay
        subjectType="teamMember"
        scoreType="processAdoption"
        teamId={teamId}
        userId={tm.cognitoId}
        fromDate={new Date(new Date().getTime() - 7 * 24 * 3600 * 1000).toISOString().substring(0, 10)}
        toDate={new Date().toISOString().substring(0, 10)}
      />
    ),
  }));

  userData.sort(teamMemberSortFn);

  const meetingData = data.meetings.map((m) => ({
    ...m,
    duration:
      m.startTime && m.endTime
        ? `${((new Date(m.endTime).getTime() - new Date(m.startTime).getTime()) / (1000 * 60)).toFixed(0)}min`
        : '-',
    attendanceDescription: m.attendance.expected > 0 ? `${m.attendance.present} / ${m.attendance.expected}` : '-',
  }));

  return (
    <div className={styles.team}>
      <h1>Team: {data.label}</h1>
      <ConfirmationDialog
        isOpen={isDeleteDialogOpen}
        close={() => setIsDeleteDialogOpen(false)}
        onConfirm={handleDelete}
        title="Are you sure?"
        description={`You are about to permanently delete the team "${data.label}". All users who are members of this team will be removed from the team, but their user accounts or any other teams they belong to will remain operational.`}
        confirmText="Delete Team"
        confirmButtonColor="error"
      />
      {formAlert && <FormAlert alert={formAlert} />}
      <div className={styles.cards}>
        <div className={styles.card}>
          <div className={styles.row}>
            <div className={styles.stack}>
              <div className={styles.score}>
                <ScoreDisplay scoreType="performance" subjectType="team" teamId={teamId} grow />
              </div>
              <h3>All time</h3>
            </div>
            <div className={styles.stack}>
              <div className={styles.score}>
                <ScoreDisplay
                  scoreType="performance"
                  subjectType="team"
                  teamId={teamId}
                  fromDate={new Date(new Date().getTime() - 6 * 7 * 24 * 3600 * 1000).toISOString().substring(0, 10)}
                  toDate={new Date().toISOString().substring(0, 10)}
                  grow
                />
              </div>
              <h3>Past 6 weeks</h3>
            </div>
            <div className={styles.stack}>
              <div className={styles.score}>
                <ScoreDisplay
                  scoreType="performance"
                  subjectType="team"
                  teamId={teamId}
                  fromDate={new Date(new Date().getTime() - 7 * 24 * 3600 * 1000).toISOString().substring(0, 10)}
                  toDate={new Date().toISOString().substring(0, 10)}
                  grow
                />
              </div>
              <h3>Past week</h3>
            </div>
          </div>
          <h2>Team Performance Score</h2>
        </div>
        <div className={styles.card}>
          <div className={styles.row}>
            <div className={styles.stack}>
              <div className={styles.score}>
                <ScoreDisplay scoreType="processAdoption" subjectType="team" teamId={teamId} grow />
              </div>
              <h3>All time</h3>
            </div>
            <div className={styles.stack}>
              <div className={styles.score}>
                <ScoreDisplay
                  scoreType="processAdoption"
                  subjectType="team"
                  teamId={teamId}
                  fromDate={new Date(new Date().getTime() - 6 * 7 * 24 * 3600 * 1000).toISOString().substring(0, 10)}
                  toDate={new Date().toISOString().substring(0, 10)}
                  grow
                />
              </div>
              <h3>Past 6 weeks</h3>
            </div>
            <div className={styles.stack}>
              <div className={styles.score}>
                <ScoreDisplay
                  scoreType="processAdoption"
                  subjectType="team"
                  teamId={teamId}
                  fromDate={new Date(new Date().getTime() - 7 * 24 * 3600 * 1000).toISOString().substring(0, 10)}
                  toDate={new Date().toISOString().substring(0, 10)}
                  grow
                />
              </div>
              <h3>Past week</h3>
            </div>
          </div>
          <h2>Team Process Adoption Score</h2>
        </div>
      </div>
      <div className={styles.cards}>
        <div className={styles.card}>
          <div className={styles.row}>
            <div className={styles.stack}>
              <div className={styles.score}>
                <ScoreDisplay id={uuid()} subjectType="organisation" scoreType="performance" grow />
              </div>
              <h3>All time</h3>
            </div>
            <div className={styles.stack}>
              <div className={styles.score}>
                <ScoreDisplay
                  subjectType="organisation"
                  scoreType="performance"
                  fromDate={new Date(new Date().getTime() - 6 * 7 * 24 * 3600 * 1000).toISOString().substring(0, 10)}
                  toDate={new Date().toISOString().substring(0, 10)}
                  grow
                />
              </div>
              <h3>Past 6 weeks</h3>
            </div>
            <div className={styles.stack}>
              <div className={styles.score}>
                <ScoreDisplay
                  subjectType="organisation"
                  scoreType="performance"
                  fromDate={new Date(new Date().getTime() - 7 * 24 * 3600 * 1000).toISOString().substring(0, 10)}
                  toDate={new Date().toISOString().substring(0, 10)}
                  grow
                />
              </div>
              <h3>Past week</h3>
            </div>
          </div>
          <h2>Organisation Performance Score</h2>
        </div>
        <div className={styles.card}>
          <div className={styles.row}>
            <div className={styles.stack}>
              <div className={styles.score}>
                <ScoreDisplay scoreType="processAdoption" subjectType="organisation" grow />
              </div>
              <h3>All time</h3>
            </div>
            <div className={styles.stack}>
              <div className={styles.score}>
                <ScoreDisplay
                  scoreType="processAdoption"
                  subjectType="organisation"
                  fromDate={new Date(new Date().getTime() - 6 * 7 * 24 * 3600 * 1000).toISOString().substring(0, 10)}
                  toDate={new Date().toISOString().substring(0, 10)}
                  grow
                />
              </div>
              <h3>Past 6 weeks</h3>
            </div>
            <div className={styles.stack}>
              <div className={styles.score}>
                <ScoreDisplay
                  scoreType="processAdoption"
                  subjectType="organisation"
                  fromDate={new Date(new Date().getTime() - 7 * 24 * 3600 * 1000).toISOString().substring(0, 10)}
                  toDate={new Date().toISOString().substring(0, 10)}
                  grow
                />
              </div>
              <h3>Past week</h3>
            </div>
          </div>
          <h2>Organisation Process Adoption Score</h2>
        </div>
      </div>
      <div>
        <Table
          rows={userData}
          keyColumn="cognitoId"
          topButtons={
            groups.includes('administrators')
              ? [
                  {
                    label: 'Edit Team',
                    to: 'edit',
                    startIcon: <EditOutlinedIcon />,
                  },
                  {
                    label: 'Delete Team',
                    handler: () => {
                      setIsDeleteDialogOpen(true);
                    },
                    startIcon: <DeleteForeverOutlinedIcon />,
                    isLoading: isDeleting,
                    color: 'error',
                  },
                ]
              : undefined
          }
          allowSort
          linkFunction={(id) => `teamMembers/${id}`}
          title={`Team members`}
          columns={[
            {
              id: 'firstName',
              label: 'First Name',
              numeric: false,
            },
            {
              id: 'lastName',
              label: 'Last Name',
              numeric: false,
            },
            {
              id: 'isLead',
              label: 'Team Leader',
              boolean: true,
            },
            {
              id: 'isEnabled',
              label: 'Enabled',
              boolean: true,
            },
            {
              id: 'performanceScore',
              label: 'Performance (past 6 weeks)',
              component: true,
            },
            {
              id: 'processAdoptionScore',
              label: 'Process Adoption (past week)',
              component: true,
            },
          ]}
        />
      </div>
      <div>
        <Table
          rows={meetingData}
          keyColumn="id"
          allowSort
          defaultSortKey="scheduledTime"
          defaultSortOrder="desc"
          allowPagination
          pageSize={10}
          linkFunction={(id) => `meetings/${id}/details`}
          title={`Meetings`}
          columns={[
            {
              id: 'status',
              label: 'Status',
              enum: MEETING_STATUS,
            },
            {
              id: 'scheduledTime',
              label: 'Scheduled time',
              date: true,
            },
            {
              id: 'startTime',
              label: 'Start time',
              date: true,
            },
            {
              id: 'duration',
              label: 'Duration',
            },
            {
              id: 'attendanceDescription',
              label: 'Attendance',
            },
            {
              id: 'commitmentCount',
              label: 'Commitments',
            },
          ]}
        />
      </div>
    </div>
  );
}
